import {Regulation, UserRole} from 'types';
import React, {useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {REGULATIONS_TRANSLATION_NAMESPACE} from '../../Regulations/regulationsConstants';
import {
	InfinityList,
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListFilter,
	InfinityListItemWithId,
	InfinityListURLParams,
	InfinityListView,
} from 'components/InfinityList';
import {
	GetRegulationsQueryQuery,
	useGetRegulationsNextLazyQuery,
	useGetRegulationsQueryLazyQuery,
	useGetRegulationsSortOrderLazyQuery,
} from '../../Regulations/hooks/regulationsInfinity.generated';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {SelectionMode} from '@fluentui/react';
import {RegulationNumberCell} from '../../Regulations/components/RegulationsList/RegulationNumberCell';
import {
	renderArrayField,
	renderAttachments,
} from 'components/EntityList/ColumnRenderers';
import {renderKeywordsCell} from 'components/Keywords/Keywords';
import {parseTooltipNewlines} from 'helpers/tooltips';
import {PAGE_PATHS} from 'pages';
import {v4 as uuidv4} from 'uuid';
import {
	useRegulationsPageColumn,
	useRegulationsPageColumnMetaData,
	useRegulationsPageFilterOptions,
} from 'features/Regulations/RegulationsPageColumns';
import {useInfinityListStandardHooks} from 'components/InfinityList/InfinitListStandardHooks';
import {GetVehicleProjectDetailsQuery} from '../hooks';
import {useGetVehicleProjectRegulationInfinityLazyQuery} from '../hooks/AssignedRegulationsInfinity.generated';
import {ProjectDetailsPageParams} from '../vehicleProjectPagePaths';

const initialSortField = 'regulationNumber';
const initialSortOrder = 'ASC';
const intBatchSize = 50;

export type VehicleProjectDetailsTabProps = {
	dataVehPro: GetVehicleProjectDetailsQuery | undefined;
	hidden: boolean;
};

const VehicleProjectDetailsTabRegulations: React.FC<
	VehicleProjectDetailsTabProps
> = ({dataVehPro, hidden}) => {
	const {t: tTitle} = useTranslation(REGULATIONS_TRANSLATION_NAMESPACE, {
		keyPrefix: 'RegulationDetail',
	});
	const {t: tViews} = useTranslation('features/regulations', {
		keyPrefix: 'Views',
	});

	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;

	const [getSortOrder] = useGetRegulationsSortOrderLazyQuery();

	const [getRegulationsQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetVehicleProjectRegulationInfinityLazyQuery();

	const [getRegulationsNext, {loading: loadingDocuments}] =
		useGetRegulationsNextLazyQuery();

	const navigate = useNavigate();

	const views: InfinityListView[] = useMemo(() => {
		return [
			{
				headerText: tViews('STD'),
				accessKey: 'STD',
				query: '',
			},
		];
	}, [dataVehPro]);

	const initialView = 'STD';

	const arrIds = useMemo(
		() => dataQuery?.vehicleProjectMasterRegulationsQuery.regulationIds ?? [],
		[dataQuery],
	);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		const {data: dataQuery} = await getRegulationsQuery({
			variables: {
				queryArgAssignedRegulations: {
					modelSeries,
					generation,
					queryAssignedReq: '{}',
					queryArgs: {query, sortField, sortOrder},
				},
			},
		});
		const arrDocumentNext =
			dataQuery?.vehicleProjectMasterRegulationsQuery.regulationsBatch ?? [];
		const arrIDsNew =
			dataQuery?.vehicleProjectMasterRegulationsQuery.regulationIds ?? [];

		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getRegulationsNext({
			variables: {nextArgs: {preFilteredIds}},
		});

		return dataDocumentsNext?.regulationsNext ?? [];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortArgs: {sortField, preFilteredIds},
			},
		});

		return dataSortOrder?.regulationsSortOrder ?? [];
	};

	const columns = useRegulationsPageColumn();
	const columnMetaData = useRegulationsPageColumnMetaData();
	const columnFilterOptions = useRegulationsPageFilterOptions({
		regulationsQuery: {
			regulationIds: [],
			regulationsBatch: [],
			regulationFilterOptions:
				dataQuery?.vehicleProjectMasterRegulationsQuery
					?.regulationFilterOptions ?? [],
		},
	} as GetRegulationsQueryQuery);

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	});

	const handleViewingItem = (item: Regulation) => {
		navigate({
			pathname: `/regulations/${item.id}`,
		});
	};

	const createHref = (item: unknown) =>
		`${PAGE_PATHS.regulations}/${(item as InfinityListItemWithId).id}`;

	const numberOfElements = useMemo(
		() => arrIds.length,
		[dataQuery, arrDocument],
	);

	return (
		<>
			{!hidden && (
				<InfinityList
					pageName={tTitle('Regulations')}
					items={arrDocument}
					sticky={true}
					columns={columns}
					columnMetaData={columnMetaData}
					columnFilterOptions={columnFilterOptions}
					selectionMode={SelectionMode.multiple}
					stillLoading={loadingQuery || loadingDocuments}
					initalFilters={linksFilters}
					initialSortField={initialSortField}
					initialSortOrder={initialSortOrder}
					initialView={initialView}
					keyPrefix={keyPrefix}
					firstRender={firstRenderSTD}
					loadItemsNext={loadItemsNextSTD}
					applyFilter={applyFilterSTD}
					applySort={applySortSTD}
					handleInvokeItem={handleViewingItem}
					createHref={createHref}
					numberOfElements={numberOfElements}
					showHeader={false}
					views={views}
				/>
			)}
		</>
	);
};

export default VehicleProjectDetailsTabRegulations;
